import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import {
  fetchDashboardData,
  fetchTradingViewAccess,
  updateUserProfile
} from '../../../api/dashboard';
import { getUserProfileAsync, setLocalToken } from '../../../redux-toolkit/store/reducers/auth.reducer';
import { useDispatch } from 'react-redux';
import Card from 'components/card';
import TradingViewAccess from './TradingViewAccess';
import { MdOutlineModeEdit, MdCheck, MdClose, MdError, MdPerson, MdLock } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import tokenService from '../../../services/tokenService';
import axios from 'axios';

const REACT_APP_BASE_URL = process.env.REACT_APP_BASE_URL;

// Default Avatar SVG Component
const DefaultAvatar = () => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 24 24"
    className="h-12 w-12"
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path fill="none" d="M0 0h24v24H0z"></path>
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"></path>
  </svg>
);

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [fullName, setFullName] = useState('');
  const [username, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  
  // Add password update state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Check for authentication
  const token = sessionStorage.getItem('token');
  const isAuthenticated = !!token;

  // Debug authentication state on component mount
  useEffect(() => {
    console.log("Profile component mounted, checking auth:");
    tokenService.debugTokenStorage();
  }, []);

  // Redirect if not authenticated after a short delay to allow token check
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!isAuthenticated) {
        console.log("Not authenticated, redirecting to login");
        navigate('/login');
      } else {
        console.log("User is authenticated, showing profile");
      }
    }, 500); // Small delay to allow token service to initialize
    
    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, navigate]);

  // Use React Query for fetching dashboard data
  const { 
    data: dashboardData, 
    isLoading: isDashboardLoading, 
    error: dashboardError,
    refetch: refetchDashboard
  } = useQuery('dashboardData', fetchDashboardData, {
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    enabled: isAuthenticated, // Only run if authenticated
    retry: 1,
    onError: (err) => {
      console.error("Dashboard data fetch error:", err);
      // Only redirect for auth errors
      if (err.message?.toLowerCase().includes('logged in') || 
          err.message?.toLowerCase().includes('unauthorized') ||
          err.response?.status === 401) {
        console.log("Auth error in dashboard fetch, redirecting");
        navigate('/login');
      }
    }
  });

  // Use React Query for fetching TradingView access data
  const { 
    data: tvAccessData, 
    isLoading: isTvAccessLoading, 
    error: tvAccessError,
    refetch: refetchTvAccess
  } = useQuery('tradingViewAccess', fetchTradingViewAccess, {
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    enabled: isAuthenticated, // Only run if authenticated
    retry: 1,
    onError: (err) => {
      console.error("TradingView access fetch error:", err);
      // Only redirect for auth errors
      if (err.message?.toLowerCase().includes('logged in') || 
          err.message?.toLowerCase().includes('unauthorized') ||
          err.response?.status === 401) {
        console.log("Auth error in TradingView fetch, redirecting");
        navigate('/login');
      }
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated) {
        console.log("Skipping profile fetch - not authenticated");
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        console.log("Fetching user profile...");
        const response = await dispatch(getUserProfileAsync());
        console.log("Profile response:", response);
        
        // Handle both raw response and unwrapped payload
        const payload = response.payload || response;
        
        // Extract user data from the response or payload
        if (payload) {
          setUser(payload);
          
          // Set all user data from the response
          setFullName(payload.fullName || payload.name || '');
          setUserName(payload.username || '');
          setEmail(payload.email || '');
          setPhoto(payload.photo || payload.profileImage || '');
          
          console.log("Set user data:", {
            fullName: payload.fullName,
            username: payload.username,
            email: payload.email,
            photo: payload.photo,
            createdAt: payload.createdAt
          });
        } else {
          console.error("Empty or invalid response from getUserProfileAsync");
          console.debug("Full response:", response);
        }
        setLoading(false);
      } catch (error) {
        console.error('Profile fetch error:', error);
        setError('Failed to load profile data');
        setLoading(false);
        
        // If auth error, redirect to login
        if (error?.response?.status === 401 || 
            error?.message?.toLowerCase().includes('unauthorized')) {
          console.log("Auth error in profile fetch, redirecting");
          navigate('/login');
        }
      }
    };

    fetchData();
  }, [dispatch, isAuthenticated, navigate]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      await updateUserProfile({ fullName, email });
      setUpdateSuccess(true);
      setEditMode(false);
      
      // Update local user state
      setUser(prev => ({
        ...prev,
        fullName,
        email
      }));
      
      // Refetch dashboard data to update UI
      if (typeof refetchDashboard === 'function') {
        refetchDashboard();
      }
      
      setTimeout(() => {
        setUpdateSuccess(false);
      }, 3000);
    } catch (err) {
      console.error("Profile update error:", err);
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  // Handle password change
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value
    });
  };

  // Handle password update submission
  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setPasswordError(null);
    
    // Validate passwords
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("New passwords don't match");
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return;
    }
    
    try {
      setPasswordLoading(true);
      const token = sessionStorage.getItem('token');
      
      const response = await axios.patch(
        `${REACT_APP_BASE_URL}/api/v1/users/updateMyPassword`, 
        {
          passwordCurrent: passwordData.currentPassword,
          password: passwordData.newPassword,
          passwordConfirm: passwordData.confirmPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data && response.data.token) {
        // Update token in all storage locations
        tokenService.setToken(response.data.token);
        
        // Update redux store with new token if needed
        dispatch(setLocalToken(response.data.token));
        
        // Password updated successfully
        setPasswordSuccess(true);
        
        // Clear form
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        
        setTimeout(() => {
          setPasswordSuccess(false);
          setShowPasswordForm(false);
        }, 3000);
      }
    } catch (err) {
      console.error("Password update error:", err);
      setPasswordError(err.response?.data?.message || "Failed to update password");
    } finally {
      setPasswordLoading(false);
    }
  };

  // Show auth error if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="w-full h-full overflow-x-hidden bg-navy-900">
        <Card extra={"w-full h-full p-4"}>
          <div className="flex flex-col items-center justify-center p-6 text-center">
            <MdError className="text-red-400 text-5xl mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Authentication Required</h3>
            <p className="text-base text-gray-400 mb-4">Please log in to view your profile</p>
            <button
              onClick={() => navigate('/login')}
              className="bg-blue-600 px-5 py-3 text-white rounded-lg hover:bg-blue-700"
            >
              Go to Login
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-x-hidden bg-navy-900">
      <div className="flex flex-col gap-5 p-5">
        <div className="flex items-center mb-4">
          <h2 className="text-2xl font-bold text-white">User Profile</h2>
        </div>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <div className="col-span-1 xl:col-span-1">
            {/* User Info Card */}
            <Card extra={"w-full h-full p-4"}>
              <div className="relative mt-1 flex h-32 w-full justify-center rounded-xl bg-cover bg-gradient-to-r from-blue-800 to-navy-800">
                <div className="absolute -bottom-12 flex h-[87px] w-[87px] items-center justify-center rounded-full border-[4px] border-navy-900 bg-blue-500 overflow-hidden">
                  {photo ? (
                    <img
                      className="h-full w-full rounded-full object-cover"
                      src={photo}
                      alt={fullName || "User"}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        e.target.parentNode.innerHTML = `
                          <div class="h-full w-full rounded-full flex items-center justify-center">
                            ${DefaultAvatar().outerHTML}
                          </div>
                        `;
                      }}
                    />
                  ) : (
                    <DefaultAvatar />
                  )}
                </div>
              </div>

              <div className="mt-16 flex flex-col items-center">
                <h4 className="text-xl font-bold text-white">
                  {fullName || username || user?.name || "User"}
                </h4>
                <p className="text-base font-medium text-gray-400">
                  {email || user?.email || "No email provided"}
                </p>
              </div>

              <div className="mt-6">
                {editMode ? (
                  <form onSubmit={handleSaveProfile} className="space-y-4">
                    <div>
                      <label className="text-sm text-white">Full Name</label>
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-navy-700 px-3 py-2 shadow-sm bg-navy-800 text-white focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-white">Email</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-navy-700 px-3 py-2 shadow-sm bg-navy-800 text-white focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>
                    <div className="flex justify-end space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          setEditMode(false);
                          // Reset values
                          setFullName(user?.fullName || '');
                          setEmail(user?.email || '');
                        }}
                        className="inline-flex items-center rounded-md border border-navy-700 bg-navy-800 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-navy-700"
                      >
                        <MdClose className="mr-2 h-5 w-5" />
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none"
                      >
                        <MdCheck className="mr-2 h-5 w-5" />
                        {loading ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <h5 className="text-sm font-medium text-gray-400">Profile Information</h5>
                      <div className="mt-3 border-t border-navy-700 pt-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm font-medium text-white">Username</p>
                            <p className="text-sm text-gray-400">
                              {username || user?.username || "Not set"}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">Full Name</p>
                            <p className="text-sm text-gray-400">
                              {fullName || user?.fullName || user?.name || "Not set"}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">Email</p>
                            <p className="text-sm text-gray-400">
                              {email || user?.email || "Not set"}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">Member Since</p>
                            <p className="text-sm text-gray-400">
                              {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 
                               user?.created ? new Date(user.created).toLocaleDateString() : "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-center space-x-3">
                      <button
                        onClick={() => setEditMode(true)}
                        className="inline-flex items-center rounded-md bg-blue-900/30 px-4 py-2 text-sm font-medium text-blue-400 hover:bg-blue-900/50"
                      >
                        <MdOutlineModeEdit className="mr-2 h-5 w-5" />
                        Edit Profile
                      </button>
                      <button
                        onClick={() => setShowPasswordForm(!showPasswordForm)}
                        className="inline-flex items-center rounded-md bg-blue-900/30 px-4 py-2 text-sm font-medium text-blue-400 hover:bg-blue-900/50"
                      >
                        <MdLock className="mr-2 h-5 w-5" />
                        {showPasswordForm ? "Cancel" : "Change Password"}
                      </button>
                    </div>
                    {updateSuccess && (
                      <div className="mt-3 text-center text-sm text-green-400">
                        Profile updated successfully!
                      </div>
                    )}
                    {error && (
                      <div className="mt-3 text-center text-sm text-red-400">
                        {error}
                      </div>
                    )}
                  </div>
                )}

                {/* Password Update Form */}
                {showPasswordForm && !editMode && (
                  <div className="mt-6 border-t border-navy-700 pt-6">
                    <h5 className="text-sm font-medium text-gray-400 mb-4">Update Password</h5>
                    <form onSubmit={handlePasswordUpdate} className="space-y-4">
                      <div>
                        <label className="text-sm text-white">Current Password</label>
                        <input
                          type="password"
                          name="currentPassword"
                          value={passwordData.currentPassword}
                          onChange={handlePasswordChange}
                          className="mt-1 block w-full rounded-md border border-navy-700 px-3 py-2 shadow-sm bg-navy-800 text-white focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-sm text-white">New Password</label>
                        <input
                          type="password"
                          name="newPassword"
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange}
                          className="mt-1 block w-full rounded-md border border-navy-700 px-3 py-2 shadow-sm bg-navy-800 text-white focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          required
                          minLength={8}
                        />
                      </div>
                      <div>
                        <label className="text-sm text-white">Confirm New Password</label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange}
                          className="mt-1 block w-full rounded-md border border-navy-700 px-3 py-2 shadow-sm bg-navy-800 text-white focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          required
                        />
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={passwordLoading}
                          className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none"
                        >
                          <MdLock className="mr-2 h-5 w-5" />
                          {passwordLoading ? 'Updating...' : 'Update Password'}
                        </button>
                      </div>
                      {passwordError && (
                        <div className="mt-2 text-sm text-red-400 text-center">
                          {passwordError}
                        </div>
                      )}
                      {passwordSuccess && (
                        <div className="mt-2 text-sm text-green-400 text-center">
                          Password updated successfully!
                        </div>
                      )}
                    </form>
                  </div>
                )}
              </div>
            </Card>
          </div>

          <div className="col-span-1 xl:col-span-1">
            {/* TradingView Access */}
            <Card extra={"!p-[20px] h-full"}>
              <TradingViewAccess 
                accessData={tvAccessData?.data} 
                isLoading={isTvAccessLoading} 
                error={tvAccessError} 
                showHeader={true}
              />
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
